// QueryParamsProvider.tsx
// Reads allowed query params once from URL, exposes them via context,
// strips them from the URL, and clears them when requested.
// This is for when a redirect is requested to the contracts app

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type QueryParams = Record<string, string>;
const ALLOWLIST = new Set(["redirect", "docCode", "doccode"]);

interface QueryParamsContextType {
  params: QueryParams;
  clearQueryParams: () => void;
}

const QueryParamsContext = createContext<QueryParamsContextType>({
  params: {},
  clearQueryParams: () => {}
});

export function QueryParamsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useState<QueryParams>({});

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const next: QueryParams = {};

    qp.forEach((value, key) => {
      if (ALLOWLIST.has(key)) {
        // Transform "doccode" to "docCode" for consistency
        const normalizedKey = key === "doccode" ? "docCode" : key;
        next[normalizedKey] = value; 
      }
    });

    if (Object.keys(next).length > 0) {
      setParams(next);
      // Strip the query from the URL
      navigate({ pathname: location.pathname }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const clearQueryParams = () => {
    setParams({});
  };

  const value = useMemo(() => ({ params, clearQueryParams }), [params]);

  return (
    <QueryParamsContext.Provider value={value}>
      {children}
    </QueryParamsContext.Provider>
  );
}

export function useQueryParams() {
  return useContext(QueryParamsContext).params;
}

export function useQueryParamsActions() {
  return useContext(QueryParamsContext);
}
